import { useI18n, SUPPORTED_LANGS } from "@/lib/i18n";

/**
 * Global 3-way language selector (EN · हिन्दी · ગુજરાતી).
 *
 * Renders as a compact segmented control on desktop and a wrapping row on
 * mobile. It is safe to place inside the top header, mobile navigation
 * panel and every login/dashboard shell — layout uses `inline-flex` +
 * `min-width: 0` so it never forces horizontal overflow, and each option
 * has a visible focus ring for keyboard users.
 *
 * Selection persists via `useI18n` (localStorage) and updates the HTML
 * `lang` attribute automatically. It never reloads the page, resets form
 * state, or logs out the user.
 *
 * Props:
 *   • variant  — "segmented" (default), "chip", or "compact"
 *   • testId   — root data-testid for QA drivers
 *   • className — extra Tailwind classes for positioning
 */

const LABELS = {
  segmented: {
    en: { long: "English", short: "EN" },
    hi: { long: "हिन्दी", short: "हि" },
    gu: { long: "ગુજરાતી", short: "ગુ" },
  },
};

export default function LanguageSelector({
  variant = "segmented",
  testId = "lang-selector",
  className = "",
  showLabelOnMobile = true,
}) {
  const { lang, setLang } = useI18n();

  const base =
    "inline-flex items-stretch rounded-full border border-[#D4AF37]/40 bg-[#04102B]/70 backdrop-blur-sm p-0.5 shadow-[0_2px_10px_rgba(0,0,0,0.25)] max-w-full";
  const btn =
    "px-2.5 sm:px-3 py-1.5 text-[11px] sm:text-xs font-semibold rounded-full transition min-w-0 truncate focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37] focus-visible:ring-offset-1 focus-visible:ring-offset-[#04102B]";
  const active =
    "bg-gradient-to-b from-[#D4AF37] to-[#B8912A] text-[#04102B] shadow-inner";
  const idle = "text-white/80 hover:text-[#F0D788] hover:bg-white/[0.04]";

  return (
    <div
      role="group"
      aria-label="Select website language"
      data-testid={testId}
      className={`${base} ${className}`}
    >
      {SUPPORTED_LANGS.map((code) => {
        const isActive = lang === code;
        const l = LABELS.segmented[code];
        return (
          <button
            key={code}
            type="button"
            data-testid={`${testId}-${code}`}
            aria-pressed={isActive}
            aria-label={`Switch to ${l.long}`}
            onClick={() => setLang(code)}
            className={`${btn} ${isActive ? active : idle}`}
          >
            <span className={showLabelOnMobile ? "hidden xs:inline sm:inline" : "hidden sm:inline"}>
              {l.long}
            </span>
            <span className={showLabelOnMobile ? "xs:hidden sm:hidden" : "sm:hidden"}>
              {l.short}
            </span>
          </button>
        );
      })}
    </div>
  );
}
